import React, { useState } from "react";
import { X, Sparkles, RefreshCw, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import { bulkCreateCOs } from "../../../../services/program.service";

// Phase 2c follow-up — AI-suggest COs review modal.
//
// Why this exists: bulkCreateCOs is now an UPSERT by (course, code) — a
// suggestion whose code matches an existing CO (active or soft-deleted)
// rewrites that CO in place (same _id, mappings stay valid). Since that's a
// destructive overwrite for active COs, the user must review + accept each
// suggestion here first; nothing is saved until "Accept selected" is clicked.

const BLOOM_LEVELS = [
  { value: "", label: "(unset)" },
  { value: "remember", label: "Remember" },
  { value: "understand", label: "Understand" },
  { value: "apply", label: "Apply" },
  { value: "analyze", label: "Analyze" },
  { value: "evaluate", label: "Evaluate" },
  { value: "create", label: "Create" },
];

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const AISuggestCOsPreview = ({
  courseId,
  suggestions = [],
  existingCodes = [],
  isMock = false,
  onClose,
}) => {
  const existingSet = new Set((existingCodes || []).map((c) => String(c).toUpperCase()));

  const [items, setItems] = useState(
    suggestions.map((s, i) => ({
      include: true,
      code: (s.code || `CO${i + 1}`).toUpperCase(),
      statement: s.statement || "",
      bloomLevel: (s.bloomLevel || "").toLowerCase(),
      topics: Array.isArray(s.topics) ? s.topics.join(", ") : "",
      order: s.order ?? i + 1,
    }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  const includedCount = items.filter((it) => it.include).length;

  const handleAccept = async () => {
    setError(null);
    const selected = items.filter((it) => it.include);

    if (selected.length === 0) {
      setError("Select at least one CO to accept.");
      return;
    }
    for (const it of selected) {
      if (!it.code.trim() || !it.statement.trim()) {
        setError("Each selected CO needs a code and a statement.");
        return;
      }
      if (!/^[A-Z0-9]{2,10}$/.test(it.code.trim().toUpperCase())) {
        setError(`"${it.code}" is not a valid code (A-Z, 0-9, 2-10 chars).`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = selected.map((it) => ({
        code: it.code.trim().toUpperCase(),
        statement: it.statement.trim(),
        bloomLevel: it.bloomLevel,
        topics: it.topics
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        order: Number(it.order) || 0,
      }));

      const result = await bulkCreateCOs(courseId, payload);
      const parts = [];
      if (result.created) parts.push(`${result.created} created`);
      if (result.updated) parts.push(`${result.updated} updated`);
      toast.success(
        `${parts.join(", ") || "Saved"}${
          isMock ? " (demo data — start Python AI for real suggestions)" : ""
        }`
      );
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => !submitting && onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>AI Suggestion</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent1" />
                Review AI-drafted Course Outcomes
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 max-w-lg">
                Edit anything before accepting. COs whose code matches an
                existing one will{" "}
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  overwrite that CO's content
                </span>{" "}
                (same ID — mappings stay intact). New codes are added as new
                COs. Uncheck any you don't want to save.
              </p>
              {isMock && (
                <p className="mt-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
                  Demo data — start the Python AI service for real suggestions.
                </p>
              )}
            </div>
            <button
              onClick={() => onClose(false)}
              disabled={submitting}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex-shrink-0"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto">
            {items.map((it, idx) => {
              const willOverwrite = existingSet.has(it.code.trim().toUpperCase());
              return (
                <div
                  key={idx}
                  className={cn(
                    "rounded-sm border p-4 transition-colors",
                    it.include
                      ? "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                      : "border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={it.include}
                      onChange={(e) => updateItem(idx, "include", e.target.checked)}
                      className="mt-1.5 accent-current flex-shrink-0"
                      aria-label={`Include ${it.code}`}
                    />
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1">
                            Code
                          </label>
                          <input
                            type="text"
                            value={it.code}
                            onChange={(e) =>
                              updateItem(idx, "code", e.target.value.toUpperCase())
                            }
                            disabled={!it.include}
                            className={inputBase}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1">
                            Bloom Level
                          </label>
                          <select
                            value={it.bloomLevel}
                            onChange={(e) =>
                              updateItem(idx, "bloomLevel", e.target.value)
                            }
                            disabled={!it.include}
                            className={inputBase}
                          >
                            {BLOOM_LEVELS.map((b) => (
                              <option key={b.value} value={b.value}>
                                {b.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1">
                          Statement
                        </label>
                        <textarea
                          value={it.statement}
                          onChange={(e) =>
                            updateItem(idx, "statement", e.target.value)
                          }
                          disabled={!it.include}
                          rows={2}
                          className={cn(inputBase, "resize-y")}
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1">
                          Topics
                        </label>
                        <input
                          type="text"
                          value={it.topics}
                          onChange={(e) => updateItem(idx, "topics", e.target.value)}
                          disabled={!it.include}
                          placeholder="Comma-separated, optional"
                          className={inputBase}
                        />
                      </div>

                      {willOverwrite && it.include && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
                          <RefreshCw className="h-3.5 w-3.5" />
                          Will overwrite existing {it.code.trim().toUpperCase()}
                        </div>
                      )}
                      {!willOverwrite && it.include && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          <Plus className="h-3.5 w-3.5" />
                          Will be added as a new CO
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => onClose(false)}
              disabled={submitting}
              className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={submitting || includedCount === 0}
              className={cn(
                "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                submitting || includedCount === 0
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:opacity-90"
              )}
            >
              <Sparkles className="h-4 w-4" />
              {submitting
                ? "Saving…"
                : `Accept ${includedCount} selected`}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AISuggestCOsPreview;
